import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Typo from 'constants/typography'

const ZoomImage = ({ src, alt, labels, onPrevious, onNext }) => {
  const stage = useRef(null)
  const image = useRef(null)
  const gesture = useRef({
    points: new Map(),
    start: null,
    pinch: null,
    lastTap: 0,
  })
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 })
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const constrain = useCallback(({ scale, x, y }) => {
    const maxX = Math.max(
      0,
      ((image.current?.clientWidth || 0) * scale -
        (stage.current?.clientWidth || 0)) /
        2,
    )
    const maxY = Math.max(
      0,
      ((image.current?.clientHeight || 0) * scale -
        (stage.current?.clientHeight || 0)) /
        2,
    )
    return {
      scale,
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    }
  }, [])
  const zoom = (scale) =>
    setTransform((value) =>
      constrain({ ...value, scale: Math.max(1, Math.min(3, scale)) }),
    )
  const reset = () => setTransform({ scale: 1, x: 0, y: 0 })
  useEffect(() => {
    const resize = () => setTransform((value) => constrain(value))
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [constrain])
  const pointerDown = (event) => {
    if (event.pointerType === 'touch') event.preventDefault()
    const data = gesture.current
    data.points.set(event.pointerId, { x: event.clientX, y: event.clientY })
    event.currentTarget.setPointerCapture?.(event.pointerId)
    if (data.points.size === 1)
      data.start = {
        x: event.clientX,
        y: event.clientY,
        transform,
        moved: false,
        type: event.pointerType,
      }
    if (data.points.size === 2) {
      const [a, b] = [...data.points.values()]
      data.pinch = {
        distance: Math.hypot(a.x - b.x, a.y - b.y),
        scale: transform.scale,
      }
      data.start = null
    }
  }
  const pointerMove = (event) => {
    const data = gesture.current
    if (!data.points.has(event.pointerId)) return
    data.points.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (data.points.size === 2 && data.pinch) {
      const [a, b] = [...data.points.values()]
      zoom(
        (data.pinch.scale * Math.hypot(a.x - b.x, a.y - b.y)) /
          (data.pinch.distance || 1),
      )
    } else if (data.points.size === 1 && data.start) {
      const dx = event.clientX - data.start.x
      const dy = event.clientY - data.start.y
      if (Math.hypot(dx, dy) > 7) data.start.moved = true
      if (transform.scale > 1)
        setTransform(
          constrain({
            scale: transform.scale,
            x: data.start.transform.x + dx,
            y: data.start.transform.y + dy,
          }),
        )
    }
  }
  const pointerEnd = (event) => {
    const data = gesture.current
    data.points.delete(event.pointerId)
    if (data.pinch) {
      if (!data.points.size) {
        data.pinch = null
        data.start = null
      }
      return
    }
    if (data.start && !data.points.size) {
      const dx = event.clientX - data.start.x
      const dy = event.clientY - data.start.y
      if (
        transform.scale === 1 &&
        Math.abs(dx) > 45 &&
        Math.abs(dx) > Math.abs(dy) * 1.25
      ) {
        if (dx < 0) onNext()
        else onPrevious()
      } else if (!data.start.moved && data.start.type === 'touch') {
        const now = Date.now()
        if (now - data.lastTap < 330) {
          zoom(transform.scale > 1 ? 1 : 2)
          data.lastTap = 0
        } else data.lastTap = now
      }
      data.start = null
    }
  }
  return (
    <div className="pdp-gallery-zoom">
      <div
        className="pdp-gallery-zoom-stage"
        ref={stage}
        role="presentation"
        onPointerDown={pointerDown}
        onPointerMove={pointerMove}
        onPointerUp={pointerEnd}
        onPointerCancel={() => {
          gesture.current.points.clear()
          gesture.current.start = null
          gesture.current.pinch = null
        }}
        onDoubleClick={() => zoom(transform.scale > 1 ? 1 : 2)}
      >
        {failed ? (
          <div role="alert">
            {labels('unavailable')}
            <button
              type="button"
              onClick={() => {
                setFailed(false)
                setAttempt((value) => value + 1)
              }}
            >
              {labels('retry')}
            </button>
          </div>
        ) : (
          <img
            key={attempt}
            ref={image}
            src={src}
            alt={alt}
            draggable={false}
            onError={() => setFailed(true)}
            style={{
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            }}
          />
        )}
      </div>
      <div className="pdp-gallery-zoom-controls">
        <button
          type="button"
          aria-label={labels('zoomOut')}
          disabled={transform.scale <= 1 || failed}
          onClick={() => zoom(transform.scale - 0.5)}
        >
          −
        </button>
        <span className={Typo.caption} aria-live="polite">
          {Math.round(transform.scale * 100)}%
        </span>
        <button
          type="button"
          aria-label={labels('zoomIn')}
          disabled={transform.scale >= 3 || failed}
          onClick={() => zoom(transform.scale + 0.5)}
        >
          +
        </button>
        <button type="button" onClick={reset}>
          {labels('reset')}
        </button>
      </div>
      <p className={`pdp-gallery-zoom-hint ${Typo.caption}`}>
        {labels(transform.scale > 1 ? 'panHint' : 'zoomHint')}
      </p>
    </div>
  )
}
ZoomImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  labels: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
}
export default ZoomImage
